import { useEffect, useState } from 'react';

const SWATCHES = ['#25398f', '#e51126', '#fec940', '#5E7B4C', '#C4653F', '#507BB2', '#EFE7D6'];
const WORD = 'VERTEX';
const MIN_DURATION = 1900; // long enough to read as a brand moment, short enough never to annoy
const SESSION_KEY = 'vp-loader-shown';

type Phase = 'loading' | 'exiting' | 'done';

const reducedMotion = () =>
  typeof window !== 'undefined' && 'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Branded first-visit loader.
 *
 * Progress eases towards 90% on a timer and only reaches 100% once the
 * window has actually finished loading (and the minimum duration has passed),
 * then a two-panel curtain lifts to reveal the page. Shown once per browser
 * session; reduced-motion visitors get a short fade instead.
 */
const Loader = () => {
  const [phase, setPhase] = useState<Phase>(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) ? 'done' : 'loading';
    } catch {
      return 'loading';
    }
  });
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (phase !== 'loading') return;
    const quick = reducedMotion();
    const started = performance.now();
    let loaded = document.readyState === 'complete';
    const onLoad = () => {
      loaded = true;
    };
    window.addEventListener('load', onLoad);
    document.body.style.overflow = 'hidden';

    const tick = window.setInterval(() => {
      const elapsed = performance.now() - started;
      const ready = loaded && elapsed >= (quick ? 300 : MIN_DURATION);
      setProgress((p) => {
        if (ready) return Math.min(100, p + 6);
        // Ease towards 90% while waiting for the real load event.
        return Math.min(90, p + Math.max(0.6, (90 - p) * 0.06));
      });
    }, 40);

    // Hard ceiling: never hold the page for more than 6s, whatever happens.
    const ceiling = window.setTimeout(() => {
      loaded = true;
    }, 6000);

    return () => {
      window.clearInterval(tick);
      window.clearTimeout(ceiling);
      window.removeEventListener('load', onLoad);
    };
  }, [phase]);

  useEffect(() => {
    if (phase === 'loading' && progress >= 100) {
      const t = window.setTimeout(() => setPhase('exiting'), 180);
      return () => window.clearTimeout(t);
    }
    if (phase === 'exiting') {
      const t = window.setTimeout(() => {
        setPhase('done');
        document.body.style.overflow = '';
        try {
          sessionStorage.setItem(SESSION_KEY, '1');
        } catch {
          /* private mode — fine to show again */
        }
      }, reducedMotion() ? 250 : 950);
      return () => window.clearTimeout(t);
    }
  }, [phase, progress]);

  if (phase === 'done') return null;
  const exiting = phase === 'exiting';
  const pct = Math.round(progress);

  return (
    <div className={`vp-loader ${exiting ? 'is-exiting' : ''}`} role="status" aria-live="polite" aria-label={`Loading Vertex Paints, ${pct} percent`}>
      {/* Curtain panels */}
      <div className="vp-loader-panel vp-loader-panel-top" />
      <div className="vp-loader-panel vp-loader-panel-bottom" />

      <div className="vp-loader-stage">
        <div className="pointer-events-none absolute -top-40 -left-32 w-[34rem] h-[34rem] rounded-full bg-brand-blue/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -right-32 w-[30rem] h-[30rem] rounded-full bg-brand-red/25 blur-3xl" />

        <div className="relative flex flex-col items-center px-6">
          {/* Emblem inside a rotating colour ring */}
          <div className="relative w-32 h-32 sm:w-36 sm:h-36">
            <span className="vp-loader-ring" aria-hidden="true" />
            <span className="absolute inset-[7px] rounded-full bg-white shadow-lift flex items-center justify-center">
              <img src="/images/brand/vertex-emblem.png" alt="" className="w-[62%] h-[62%] object-contain vp-loader-emblem" />
            </span>
          </div>

          {/* Wordmark */}
          <div className="mt-8 flex items-baseline gap-3" aria-hidden="true">
            <span className="flex">
              {WORD.split('').map((ch, i) => (
                <span key={i} className="vp-loader-letter text-4xl sm:text-5xl font-extrabold tracking-[.12em] text-white" style={{ animationDelay: `${0.15 + i * 0.07}s` }}>
                  {ch}
                </span>
              ))}
            </span>
            <span className="vp-loader-letter text-xl sm:text-2xl font-bold tracking-[.3em] text-brand-gold" style={{ animationDelay: '.65s' }}>
              PAINTS
            </span>
          </div>

          {/* Brush stroke */}
          <svg className="mt-3 w-64 sm:w-80 h-6" viewBox="0 0 320 24" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id="vpStroke" x1="0" x2="1">
                <stop offset="0" stopColor="#25398f" />
                <stop offset=".55" stopColor="#e51126" />
                <stop offset="1" stopColor="#fec940" />
              </linearGradient>
            </defs>
            <path className="vp-loader-stroke" d="M6 16 C 60 4, 110 20, 160 11 S 262 4, 314 12" stroke="url(#vpStroke)" strokeWidth="7" strokeLinecap="round" />
          </svg>

          {/* Swatch fan */}
          <div className="mt-8 flex gap-2" aria-hidden="true">
            {SWATCHES.map((c, i) => (
              <span key={c} className="vp-loader-swatch w-6 h-9 sm:w-7 sm:h-10 rounded-md shadow-lg border border-white/20" style={{ background: c, animationDelay: `${0.8 + i * 0.08}s` }} />
            ))}
          </div>

          {/* Progress */}
          <div className="mt-10 w-64 sm:w-80">
            <div className="h-[3px] rounded-full bg-white/15 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-brand-blue via-brand-red to-brand-gold transition-[width] duration-150 ease-out" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] font-semibold tracking-[.18em] uppercase text-white/60">
              <span>{pct < 40 ? 'Mixing colours' : pct < 80 ? 'Preparing the finish' : 'Almost ready'}</span>
              <span className="tabular-nums text-white/80">{pct}%</span>
            </div>
          </div>
        </div>

        <p className="absolute bottom-8 inset-x-0 text-center text-[11px] tracking-[.22em] uppercase text-white/40">Colour that lasts · Since 2007</p>
      </div>
    </div>
  );
};

export default Loader;
